import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import useQuery from '@hooks/useQuery';
import AuthService from '@api/AuthService';
import Loader from '@components/Loader';
import routes from '@constants/routes';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { UserActions, UserSelectors } from '@store/slices/user';
import {
  Alert, Box, Button, Paper, Stack, Typography,
} from '@mui/material';
import { clearLocalAuthState, getAuthErrorMessage } from '@utils/authSession';

function getHashParams() {
  return new URLSearchParams(window.location.hash.replace(/^#/, ''));
}

type LoginProps = {
  isCallback?: boolean;
};

const AUTH_CODE_EXCHANGE_TIMEOUT_MS = 30000;

export function shouldStartAzureAuth({
  authCode,
  authError,
  hasUnsafeTokenParams,
  isAuthorized,
  isCallback,
}: {
  authCode: string | null;
  authError: string | null;
  hasUnsafeTokenParams: boolean;
  isAuthorized: boolean;
  isCallback: boolean;
}) {
  return !isAuthorized && !isCallback && !authCode && !authError && !hasUnsafeTokenParams;
}

function Login({ isCallback = false }: LoginProps) {
  const query = useQuery();
  const hashParams = useMemo(() => getHashParams(), []);
  const dispatch = useDispatch();
  const isAuthorized = useSelector(UserSelectors.isAuthorized);
  const errorMessage = query.get('errorMessage');
  const authCode = hashParams.get('authCode');
  const hasUnsafeTokenParams = Boolean(
    query.get('accessToken')
    || query.get('refreshToken'),
  );
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [exchangeComplete, setExchangeComplete] = useState(false);
  const exchangedAuthCodeRef = useRef<string | null>(null);
  const authError = errorMessage || exchangeError;

  const retryAuth = useCallback(() => {
    clearLocalAuthState();
    window.location.replace(
      AuthService.links.authWithAzure(window.location.origin),
    );
  }, []);

  useEffect(() => {
    if (hasUnsafeTokenParams) {
      clearLocalAuthState();
      window.history.replaceState(null, document.title, routes.loginPage);
      setExchangeError('unsafeTokenRedirect');
      return;
    }

    if (errorMessage) {
      clearLocalAuthState();
      return;
    }

    if (!authCode) {
      if (shouldStartAzureAuth({
        authCode,
        authError,
        hasUnsafeTokenParams,
        isAuthorized,
        isCallback,
      })) {
        window.location.replace(
          AuthService.links.authWithAzure(window.location.origin),
        );
      } else if (isCallback && !authError && !isAuthorized) {
        setExchangeError('exchangeFailed');
      }
      return;
    }

    if (exchangedAuthCodeRef.current === authCode) {
      return;
    }

    exchangedAuthCodeRef.current = authCode;

    async function exchangeAuthCode() {
      const abortController = new AbortController();
      const timeoutId = window.setTimeout(
        () => abortController.abort(),
        AUTH_CODE_EXCHANGE_TIMEOUT_MS,
      );

      try {
        clearLocalAuthState();

        const { data } = await AuthService.exchangeAzureAuthCode(
          authCode as string,
          abortController.signal,
        );
        const { accessToken, refreshToken } = data;

        localStorage.setItem('accessToken', accessToken as string);
        localStorage.setItem('refreshToken', refreshToken as string);
        window.history.replaceState(
          null,
          document.title,
          isCallback ? routes.authCallback : routes.loginPage,
        );

        dispatch(UserActions.setIsAuthorized(true));
        setExchangeComplete(true);
      } catch (_error) {
        clearLocalAuthState();
        window.history.replaceState(null, document.title, routes.loginPage);
        setExchangeError(
          abortController.signal.aborted
            ? 'exchangeTimedOut'
            : 'exchangeFailed',
        );
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    exchangeAuthCode();
  }, [
    authCode,
    authError,
    dispatch,
    errorMessage,
    hasUnsafeTokenParams,
    isAuthorized,
    isCallback,
  ]);

  const authErrorMessage = useMemo(
    () => getAuthErrorMessage(authError),
    [authError],
  );

  if (authError) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
        px={2}
      >
        <Paper sx={{ p: 4, width: '100%', maxWidth: 460 }}>
          <Stack spacing={3}>
            <Typography variant="h5" component="h1">
              Sign in
            </Typography>
            <Alert severity="error">
              {authErrorMessage}
            </Alert>
            <Button variant="contained" onClick={retryAuth}>
              Sign in again
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (isAuthorized && (!authCode || exchangeComplete)) {
    return <Navigate to={routes.dashboard} />;
  }

  return <Loader />;
}

export default Login;
